/**
 * AtmosOS Global Form Validation Utility
 * 
 * Reusable validation functions, regex patterns, and API error mappers
 * for use across all components and pages (Suppliers, Contacts, Components, Assets, etc.).
 */

// ── Standard Validation Regular Expressions ──────────────────────────────────
export const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const REGEX_TELEPHONE = /^[0-9\s()-]+$/;
export const REGEX_LETTERS_ONLY = /^[A-Za-z ]+$/;
export const REGEX_ALPHANUMERIC_SPECIAL = /^[A-Za-z0-9 @&_.-]+$/;
export const REGEX_PART_NUMBER = /^[A-Za-z0-9@&_-]+$/;
export const REGEX_ADDRESS = /^[A-Za-z0-9 @&_/.,#\-—–]+$/;
export const REGEX_CITY = /^[A-Za-z0-9\s.,'/-]+$/;
export const REGEX_POSTAL_CODE = /^[0-9-]+$/;
export const REGEX_STATUS = /^[A-Za-z /]+$/;
export const REGEX_URL = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/i;
export const REGEX_GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[A-Z0-9]{1}[0-9A-Z]{1}$/;

// ── Individual Reusable Field Validators ─────────────────────────────────────
// Each validator returns an error string if invalid, or '' (empty string) if valid.

/**
 * Validates an Email Address
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=true]
 * @param {string} [options.fieldName='Email address']
 * @returns {string} Error message or empty string
 */
export const validateEmail = (value, { required = true, fieldName = 'Email address' } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? `${fieldName} is required.` : '';
    }
    if (!REGEX_EMAIL.test(val)) {
        return 'Enter a valid email address.';
    }
    return '';
};

/**
 * Validates a Telephone / Phone number
 * Enforces digits, spaces, hyphens, parentheses only, strictly disallows '+', and counts 7-15 digits.
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=true]
 * @param {string} [options.fieldName='Telephone']
 * @returns {string} Error message or empty string
 */
export const validateTelephone = (value, { required = true, fieldName = 'Telephone' } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? `${fieldName} is required.` : '';
    }
    if (val.includes('+') || !REGEX_TELEPHONE.test(val)) {
        return `${fieldName} can contain only digits, spaces, hyphens, and parentheses. The + character is not allowed.`;
    }
    const digitCount = (val.match(/\d/g) || []).length;
    if (digitCount < 7 || digitCount > 15) {
        return `${fieldName} must contain between 7 and 15 digits.`;
    }
    return '';
};

/**
 * Validates a Letters & Spaces only field (e.g. Contact Person Name, Person Full Name)
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=true]
 * @param {string} [options.fieldName='Contact name']
 * @returns {string} Error message or empty string
 */
export const validateLettersOnly = (value, { required = true, fieldName = 'Contact name' } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? `${fieldName} is required.` : '';
    }
    if (!REGEX_LETTERS_ONLY.test(val)) {
        return `${fieldName} can contain only letters and spaces.`;
    }
    return '';
};

/**
 * Validates Alphanumeric + Special Characters (e.g. Supplier Name, Supplier Code, Role, Part Numbers)
 * Permitted: letters, numbers, spaces, @, &, ., -, and _
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=true]
 * @param {string} [options.fieldName='This field']
 * @param {RegExp} [options.pattern=REGEX_ALPHANUMERIC_SPECIAL]
 * @param {string} [options.customCharsDesc='letters, numbers, spaces, @, &, ., -, and _']
 * @returns {string} Error message or empty string
 */
export const validateAlphanumericSpecial = (
    value,
    {
        required = true,
        fieldName = 'This field',
        pattern = REGEX_ALPHANUMERIC_SPECIAL,
        customCharsDesc = 'letters, numbers, spaces, @, &, ., -, and _'
    } = {}
) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? `${fieldName} is required.` : '';
    }
    if (!pattern.test(val)) {
        return `${fieldName} can contain only ${customCharsDesc}.`;
    }
    return '';
};

/**
 * Sanitizes a Part Number or Code so that spaces and disallowed characters
 * are replaced with hyphens, conforming to the strict regex /^[A-Za-z0-9@&_-]+$/
 * @param {string} val
 * @returns {string} Sanitized string
 */
export const sanitizePartNumber = (val) => {
    if (!val) return '';
    return String(val)
        .replace(/[^A-Za-z0-9@&_-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^[-_]+|[-_]+$/g, '');
};

/**
 * Validates a Part Number against /^[A-Za-z0-9@&_-]+$/
 * (strictly letters, numbers, @, &, -, and _, no spaces)
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=true]
 * @param {string} [options.fieldName='Supplier part number']
 * @returns {string} Error message or empty string
 */
export const validatePartNumber = (value, { required = true, fieldName = 'Supplier part number' } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? `${fieldName} is required.` : '';
    }
    if (!REGEX_PART_NUMBER.test(val)) {
        return `${fieldName} can contain only letters, numbers, @, &, -, and _ (no spaces).`;
    }
    return '';
};

/**
 * Validates a Street / Physical Address
 * Permitted: letters, numbers, spaces, @, &, _, /, -
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=true]
 * @param {string} [options.fieldName='Address']
 * @returns {string} Error message or empty string
 */
export const validateAddress = (value, { required = true, fieldName = 'Address' } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? 'This field is required.' : '';
    }
    if (!REGEX_ADDRESS.test(val)) {
        return 'Enter a valid address using the permitted characters.';
    }
    return '';
};

/**
 * Validates a City name (letters and spaces only)
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=true]
 * @param {string} [options.fieldName='City']
 * @returns {string} Error message or empty string
 */
export const validateCity = (value, { required = true, fieldName = 'City' } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? 'This field is required.' : '';
    }
    if (!REGEX_CITY.test(val)) {
        return 'Use letters, numbers, spaces, and hyphens only.';
    }
    return '';
};

/**
 * Validates a Postal Code (numbers and hyphens only)
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=false]
 * @returns {string} Error message or empty string
 */
export const validatePostalCode = (value, { required = false } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? 'This field is required.' : '';
    }
    if (!REGEX_POSTAL_CODE.test(val)) {
        return 'Use numbers and hyphens only.';
    }
    return '';
};

/**
 * Validates a GST Number (GSTIN) format (15 characters)
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=false]
 * @returns {string} Error message or empty string
 */
export const validateGstNumber = (value, { required = false } = {}) => {
    const val = String(value ?? '').trim().toUpperCase();
    if (!val) {
        return required ? 'This field is required.' : '';
    }
    if (val.length !== 15) {
        return 'GST number must be exactly 15 characters (e.g. 06AAFCV5179D2Z7).';
    }
    if (!REGEX_GSTIN.test(val)) {
        return 'Invalid GST format (e.g. 06AAFCV5179D2Z7).';
    }
    return '';
};

/**
 * Validates a Website URL
 * @param {string} value
 * @param {Object} options
 * @param {boolean} [options.required=false]
 * @returns {string} Error message or empty string
 */
export const validateUrl = (value, { required = false } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? 'This field is required.' : '';
    }
    if (!REGEX_URL.test(val)) {
        return 'Enter a valid website URL.';
    }
    return '';
};

/**
 * Validates a required selection (dropdown, ID, or boolean value)
 * @param {*} value
 * @param {string} [message='This field is required.']
 * @returns {string} Error message or empty string
 */
export const validateRequiredSelect = (value, message = 'This field is required.') => {
    if (value === null || value === undefined || value === '') {
        return message;
    }
    return '';
};

// ── Supplier Document Module Validators ─────────────────────────────────────

/**
 * Validates whether a date string is in valid YYYY-MM-DD format and represents a real calendar date
 * @param {string} value
 * @returns {boolean}
 */
export const isValidDate = (value) => {
    if (!value || typeof value !== 'string') return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return false;
    }
    const date = new Date(`${value}T00:00:00.000Z`);
    return (
        !Number.isNaN(date.getTime()) &&
        date.toISOString().slice(0, 10) === value
    );
};

/**
 * Validates Document Name (letters, numbers, spaces, @, &, -, and _, max 2000 chars)
 * @param {string} value
 * @param {Object} options
 * @returns {string} Error message or empty string
 */
export const validateDocumentName = (value, { required = true, maxLength = 2000 } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? 'Document name is required.' : '';
    }
    if (val.length > maxLength) {
        return `Document name cannot exceed ${maxLength} characters.`;
    }
    if (!REGEX_ALPHANUMERIC_SPECIAL.test(val)) {
        return 'Document name can contain only letters, numbers, spaces, @, &, -, and _.';
    }
    return '';
};

/**
 * Validates Effective Date
 * @param {string} value
 * @param {Object} options
 * @returns {string} Error message or empty string
 */
export const validateEffectiveDate = (value, { required = true } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? 'Effective date is required.' : '';
    }
    if (!isValidDate(val)) {
        return 'Enter a valid effective date.';
    }
    return '';
};

/**
 * Validates Expiry Date (must be real date and strictly later than Effective Date)
 * @param {string} value
 * @param {string} effectiveDate
 * @param {Object} options
 * @returns {string} Error message or empty string
 */
export const validateExpiryDate = (value, effectiveDate, { required = true } = {}) => {
    const val = String(value ?? '').trim();
    if (!val) {
        return required ? 'Expiry date is required.' : '';
    }
    if (!isValidDate(val)) {
        return 'Enter a valid expiry date.';
    }
    const eff = String(effectiveDate ?? '').trim();
    if (eff && isValidDate(eff)) {
        if (val <= eff) {
            return 'Expiry date must be greater than effective date.';
        }
    }
    return '';
};

/**
 * Validates Document Dates matching reference specification
 * @param {string} effectiveDate
 * @param {string} expiryDate
 * @returns {null|{field: string, message: string}}
 */
export const validateDocumentDates = (effectiveDate, expiryDate) => {
    if (!effectiveDate) {
        return {
            field: 'effective_date',
            message: 'Effective date is required.'
        };
    }
    if (!expiryDate) {
        return {
            field: 'expiry_date',
            message: 'Expiry date is required.'
        };
    }
    if (!isValidDate(effectiveDate)) {
        return {
            field: 'effective_date',
            message: 'Enter a valid effective date.'
        };
    }
    if (!isValidDate(expiryDate)) {
        return {
            field: 'expiry_date',
            message: 'Enter a valid expiry date.'
        };
    }
    if (expiryDate <= effectiveDate) {
        return {
            field: 'expiry_date',
            message: 'Expiry date must be greater than effective date.'
        };
    }
    return null;
};

// ── Django REST Framework Backend Error Parser ───────────────────────────────

/**
 * Maps Django REST Framework response validation errors to form fields
 * @param {Object} errData DRF error response JSON
 * @param {Object} [customFieldMap={}] Additional field name mappings
 * @returns {Object} Field-level errors keyed by form field names
 */
export const mapDrfErrorsToFields = (errData, customFieldMap = {}) => {
    if (!errData) return {};
    const source = Array.isArray(errData) ? (errData[0] || {}) : (typeof errData === 'object' ? errData : {});
    const target = (source.errors && typeof source.errors === 'object') ? source.errors : source;

    const defaultFieldMap = {
        // Supplier fields
        supplier_code: 'code',
        code: 'code',
        supplier_name: 'name',
        name: 'name',
        registration_number: 'registrationNumber',
        registrationNumber: 'registrationNumber',
        tax_number: 'taxNumber',
        taxNumber: 'taxNumber',
        email: 'email',
        telephone: 'phone',
        phone: 'phone',
        address_line_1: 'address1',
        address1: 'address1',
        address_line_2: 'address2',
        address2: 'address2',
        city: 'city',
        country_id: 'country_id',
        country: 'country_id',
        state_id: 'state_id',
        state: 'state_id',
        postal_code: 'postalCode',
        postalCode: 'postalCode',
        website: 'website',
        payment_terms: 'payment_terms',
        warranty_terms: 'warranty_terms',
        warranty_terms_id: 'warranty_terms',
        supplier_status: 'supplierStatus',
        supplierStatus: 'supplierStatus',
        status: 'status',

        // Contact fields
        contact_name: 'name',
        contact_role: 'designation',
        designation: 'designation',
        role: 'designation',
        supplier_id: 'supplier',
        supplier: 'supplier',
        is_primary: 'isPrimary',

        // Component fields
        component_id: 'component_id',
        price: 'price',
        tax: 'tax',
        minimum_order_quantity: 'minimum_order_quantity',
        lead_time_days: 'lead_time_days',
        warranty_months: 'warranty_months',
        supplier_part_number: 'supplier_part_number',
        hsn_code: 'hsn_code',
        hsn: 'hsn_code',

        // Document fields
        document_type_id: 'document_type_id',
        document_type: 'document_type_id',
        document_name: 'document_name',
        original_filename: 'original_filename',
        filename: 'original_filename',
        file: 'original_filename',
        effective_date: 'effective_date',
        expiry_date: 'expiry_date',
    };

    const mergedFieldMap = { ...defaultFieldMap, ...customFieldMap };
    const mapped = {};

    if (typeof target === 'object' && target !== null) {
        Object.entries(target).forEach(([k, v]) => {
            const formField = mergedFieldMap[k];
            if (formField) {
                if (Array.isArray(v) && v.length > 0) {
                    mapped[formField] = v.join(' ');
                } else if (typeof v === 'string') {
                    mapped[formField] = v;
                }
            }
        });
    }
    return mapped;
};

/**
 * Extracts a human-readable error message from backend error responses
 * @param {Object} errData
 * @param {string} [fallback='An unexpected error occurred. Please try again.']
 * @returns {string}
 */
export const extractApiErrorMessage = (errData, fallback = 'An unexpected error occurred. Please try again.') => {
    if (!errData) return fallback;
    if (typeof errData === 'string') return errData;
    if (errData.message && typeof errData.message === 'string') return errData.message;
    if (errData.detail && typeof errData.detail === 'string') return errData.detail;
    if (errData.error && typeof errData.error === 'string') return errData.error;

    if (Array.isArray(errData) && errData.length > 0) {
        const first = errData[0];
        if (typeof first === 'string') return first;
        if (typeof first === 'object') return extractApiErrorMessage(first, fallback);
    }

    const targetObj = (errData.errors && typeof errData.errors === 'object') ? errData.errors : errData;
    if (typeof targetObj === 'object' && targetObj !== null) {
        const fieldErrors = [];
        for (const [key, val] of Object.entries(targetObj)) {
            if (['status', 'statusCode', 'code', 'success'].includes(key)) continue;
            let valStr = '';
            if (Array.isArray(val)) {
                valStr = val.join(', ');
            } else if (typeof val === 'string') {
                valStr = val;
            } else if (typeof val === 'object' && val !== null) {
                valStr = val.message || val.detail || val.msg || JSON.stringify(val);
            }
            if (valStr) {
                const readableKey = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                fieldErrors.push(`${readableKey}: ${valStr}`);
            }
        }
        if (fieldErrors.length > 0) {
            return fieldErrors.join(' | ');
        }
    }

    return fallback;
};
